import { z } from "zod";
import Guid from "_shared/guids/guid";
import type Session from "users/__session/session";
import {
  proposalCollaborationStorageScopeFromSession,
  proposalCollaborationViewerScope,
} from "work/proposals/negotiation/proposal-collaboration-local-store";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import ProposalReviewerSchema from "work/proposals/reviewers/_schemas/proposal-reviewer-schema";

export const PROPOSAL_UNCOMMITTED_INPUT_STORAGE_KEY = "attorneyhub.proposals.uncommittedInput.v1";

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

const StoredUncommittedInputZ = z.object({
  version: z.literal(1),
  composerDrafts: z.record(z.string(), z.string()),
  stagedReviewerSelections: z.record(z.string(), z.array(ProposalReviewerSchema.ProposalReviewerJSONZ)),
});
type StoredUncommittedInput = z.infer<typeof StoredUncommittedInputZ>;

/**
 * One durable home for user input the actor has typed or toggled but not yet committed to a save — a
 * proposal comment composer's text, and a reviewer selector's staged (unsent) selection. Both used to
 * live in state under a subtree whose mounting depended on data flow: a refetch, an Ably-driven
 * proposal replacement, or a viewer-scope change could unmount it, discarding the user's in-progress
 * work. Field edits already have this guarantee via the durable pending-operation queue
 * (`proposal-collaboration-local-store.ts`) — this module gives the same guarantee (survives a remount
 * AND a full page reload, scoped per viewer the same way that queue is) to input that is not a field
 * edit at all.
 *
 * Deliberately NOT Redux: a store write per keystroke re-renders every subscriber on the page, which is
 * what previously made typing crawl. A pair of module-level Maps gives O(1) writes with zero
 * subscribers — they outlive a component remount for free, with no store dispatch involved — and every
 * write is *also* mirrored straight to `localStorage` (bypassing Redux entirely, so no subscriber is
 * ever notified) so a full reload can rehydrate it. Reusing the collaboration queue's own scope
 * derivation (`proposalCollaborationStorageScopeFromSession` / `proposalCollaborationViewerScope`) keeps
 * there being exactly one implementation of "whose browser storage is this" (S1).
 */
let composerDrafts = new Map<string, string>();
let stagedReviewerSelections = new Map<string, ProposalReviewer[]>();
let hydratedScopeKey: string | undefined;

/** Derives the storage scope key for the current session, reusing the collaboration queue's own scope
 * derivation so a user's uncommitted comment/reviewer-selection drafts are isolated the same way its
 * pending field-edit queue already is. Falls back to a shared "anonymous" bucket when the session does
 * not yet carry enough identity to scope by (e.g. mid-login) rather than refusing to persist at all. */
export function proposalUncommittedInputScopeKey(session: Pick<Session, "context" | "user">): string {
  const scope = proposalCollaborationStorageScopeFromSession(session);
  return scope ? proposalCollaborationViewerScope(scope) : "anonymous";
}

/** Keyed by proposal alone: a negotiation has one comment composer, not one per field. */
export function composerDraftKey(proposalId?: Guid | null): string | undefined {
  if (!proposalId?.value) return undefined;
  return proposalId.value;
}

export function readComposerDraft(scopeKey: string, proposalId?: Guid | null): string {
  ensureHydrated(scopeKey);
  const key = composerDraftKey(proposalId);
  if (!key) return "";
  return composerDrafts.get(key) ?? "";
}

export function writeComposerDraft(
  scopeKey: string,
  proposalId: Guid | null | undefined,
  text: string,
): void {
  ensureHydrated(scopeKey);
  const key = composerDraftKey(proposalId);
  if (!key) return;

  if (text === "") composerDrafts.delete(key);
  else composerDrafts.set(key, text);
  persist(scopeKey);
}

export function readStagedReviewerSelection(
  scopeKey: string,
  proposalId?: Guid | null,
): ProposalReviewer[] | undefined {
  ensureHydrated(scopeKey);
  if (!proposalId?.value) return undefined;
  return stagedReviewerSelections.get(proposalId.value);
}

export function writeStagedReviewerSelection(
  scopeKey: string,
  proposalId: Guid | null | undefined,
  reviewers: ProposalReviewer[],
): void {
  ensureHydrated(scopeKey);
  if (!proposalId?.value) return;
  stagedReviewerSelections.set(proposalId.value, reviewers);
  persist(scopeKey);
}

export function clearStagedReviewerSelection(scopeKey: string, proposalId?: Guid | null): void {
  ensureHydrated(scopeKey);
  if (!proposalId?.value) return;
  stagedReviewerSelections.delete(proposalId.value);
  persist(scopeKey);
}

/**
 * Test-only: drops the in-memory mirrors and forgets the last-hydrated scope, so the next read
 * re-hydrates from `localStorage` exactly as a fresh page load would. This is how a spec simulates a
 * reload rather than a mere remount — a remount needs nothing special: the module-level Maps already
 * survive it, which is the whole point.
 */
export function resetProposalUncommittedInputStoreForTest(): void {
  composerDrafts = new Map();
  stagedReviewerSelections = new Map();
  hydratedScopeKey = undefined;
}

function ensureHydrated(scopeKey: string): void {
  if (hydratedScopeKey === scopeKey) return;
  composerDrafts = new Map();
  stagedReviewerSelections = new Map();
  hydratedScopeKey = scopeKey;

  const storage = getBrowserLocalStorage();
  if (!storage) return;

  try {
    const raw = storage.getItem(storageKey(scopeKey));
    if (!raw) return;
    const parsed = StoredUncommittedInputZ.safeParse(JSON.parse(raw));
    if (!parsed.success) return;

    for (const [key, text] of Object.entries(parsed.data.composerDrafts)) {
      composerDrafts.set(key, text);
    }
    for (const [proposalIdValue, reviewersJSON] of Object.entries(parsed.data.stagedReviewerSelections)) {
      const reviewers = reviewersJSON.flatMap((json) => {
        try {
          return [ProposalReviewer.fromJSON(json)];
        } catch {
          return [];
        }
      });
      if (reviewers.length > 0) stagedReviewerSelections.set(proposalIdValue, reviewers);
    }
  } catch {
    // Storage is an optimization. The in-memory Maps stay empty — typing/staging still works for this
    // session, it just would not survive a reload.
  }
}

function persist(scopeKey: string): void {
  const storage = getBrowserLocalStorage();
  if (!storage) return;

  try {
    if (composerDrafts.size === 0 && stagedReviewerSelections.size === 0) {
      storage.removeItem(storageKey(scopeKey));
      return;
    }

    const payload: StoredUncommittedInput = {
      version: 1,
      composerDrafts: Object.fromEntries(composerDrafts),
      stagedReviewerSelections: Object.fromEntries(
        Array.from(stagedReviewerSelections.entries(), ([proposalIdValue, reviewers]) => [
          proposalIdValue,
          reviewers.map((reviewer) => reviewer.toJSON()),
        ]),
      ),
    };
    storage.setItem(storageKey(scopeKey), JSON.stringify(payload));
  } catch {
    // Best-effort, same as the collaboration queue: in-memory state remains correct for this session
    // even when storage is unavailable or full.
  }
}

function storageKey(scopeKey: string): string {
  return `${PROPOSAL_UNCOMMITTED_INPUT_STORAGE_KEY}.${scopeKey}`;
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
